import { Log } from '@agilelab/plugin-wb-builder-common';
import {
  GenericField,
  WbCard,
  WbCardContent,
  WbLogsCard,
} from '@agilelab/plugin-wb-platform';
import { useApi } from '@backstage/core-plugin-api';
import { Box, Drawer, Theme, Typography, makeStyles } from '@material-ui/core';
import { FC, useEffect, useState } from 'react';
import { panelCatalogApiRef } from '../../api';
import { useReleaseDetailPageContext } from './context/useReleaseDetailPageContext';
import { useDagStatusColor } from './hooks/useDagStatusColor';
import { useStepIcons } from './hooks/useStepIcons';

const useStyles = makeStyles((theme: Theme) => ({
  drawerPaper: {
    height: '100%',
    width: '50%',
  },
  drawerContent: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  logTimeContainer: {
    display: 'flex',
    gap: 37,
    alignItems: 'center',
  },
  row: {
    display: 'flex',
    width: '100%',
    gap: 50,
    flexWrap: 'wrap',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    '& svg': {
      width: '24px',
      height: '24px',
      marginRight: theme.spacing(1),
    },
  },
}));

export const LogsDrawer: FC = () => {
  const dagStatusColors = useDagStatusColor();
  const stepIcons = useStepIcons();
  const panelCatalog = useApi(panelCatalogApiRef);
  const classes = useStyles();
  const { selectedDeploy, isLogsDrawerOpen, setIsLogsDrawerOpen } =
    useReleaseDetailPageContext();

  const [logs, setLogs] = useState<Log[]>([]);

  useEffect(() => {
    if (selectedDeploy && isLogsDrawerOpen) {
      const loadData = async () => {
        const logsResponse = await panelCatalog.getLogs(selectedDeploy.id);
        setLogs(logsResponse);
      };

      loadData();
    }
  }, [panelCatalog, selectedDeploy, isLogsDrawerOpen]);

  return (
    <Drawer
      anchor="right"
      classes={{
        paper: classes.drawerPaper,
      }}
      PaperProps={{
        style: {
          width: '80%',
        },
      }}
      transitionDuration={200}
      open={!!isLogsDrawerOpen}
      onClose={() => setIsLogsDrawerOpen(false)}
    >
      {selectedDeploy && (
        <Box className={classes.drawerContent}>
          <Box className={classes.header}>
            {stepIcons[selectedDeploy.status]}
            <Typography color="primary" variant="h6">
              {selectedDeploy.idRelease}
            </Typography>
          </Box>

          <WbCard title="Details">
            <WbCardContent>
              <Box className={classes.row}>
                <GenericField
                  label="Start Time"
                  value={selectedDeploy.deployDate}
                  type="date"
                />
                {selectedDeploy.deployEndDate && (
                  <GenericField
                    label="Stop Time"
                    value={selectedDeploy.deployEndDate}
                    type="date"
                  />
                )}
                <GenericField
                  label="Status"
                  value={selectedDeploy.status}
                  type="string"
                  contentProps={{
                    style: {
                      color: dagStatusColors[selectedDeploy.status],
                    },
                  }}
                />
                <GenericField
                  label="Action"
                  value={selectedDeploy.action}
                  type="string"
                />
              </Box>
            </WbCardContent>
          </WbCard>
          <WbLogsCard
            title="Log File"
            logs={logs}
            style={{ height: '100%', marginTop: '16px', overflow: 'auto' }}
          />
        </Box>
      )}
    </Drawer>
  );
};
